import React, { useEffect, useRef } from 'react';
import Moveable from 'react-moveable';
import { useDispatch, useSelector } from 'react-redux';
import { sortBy, isEqual } from 'lodash-es';
import {
  getFormEditorControlData,
  getSelectedFormEditorForm,
  onInputFieldChange,
  selectInputField,
  toggleSelectInputField,
  shiftSelectInputFields,
} from '../../store/slices/formEditorSlice';
import { selectSelectedProjectResources } from 'store/resources/selectors/resources.selectors';

const getFilledPreviewText = (item) => {
  const typeName = item.type?.name;
  switch (typeName) {
    case 'checkBox':
    case 'radioButton':
      return 'X';
    case 'fullDate':
      return '27.04.2026';
    case 'manualFullDate':
      return '27.04.2026';
    case 'dateMonthAndYear':
      return '04/26';
    case 'dateWeekNumber':
      return '17';
    case 'dateYear':
      return '2026';
    case 'dateWeekDay':
      return 'Ma';
    case 'dateTimeClock':
      return '08:00';
    case 'fillerName':
      return 'Matti Meikäläinen';
    case 'fillerPhone':
      return '040 1234567';
    case 'fillerCompany':
      return 'Yritys Oy';
    case 'fillerEmail':
      return 'matti@yritys.fi';
    case 'fillerTaxNumber':
      return '123456-1234';
    case 'fillerBirthDate':
      return '01.01.1980';
    case 'projectName':
      return 'Esimerkki projekti';
    case 'projectSiteKey':
      return 'ABC-123';
    case 'projectAddress':
      return 'Esimerkkikatu 1, Helsinki';
    case 'clientCompanyName':
      return 'Asiakas Oy';
    case 'siteContactPerson':
      return 'Ville Valvoja';
    case 'siteContactPhone':
      return '050 9876543';
    case 'runningNumber':
      return item.text?.trim() ? `${item.text.trim()} 1` : '1';
    case 'signature':
      return '';
    case 'image':
    case 'imageWithCrop':
    case 'imageAsAttachment':
      return '';
    default:
      return item.text || item.title || '';
  }
};

export const InputFieldsNew = ({
  selectedPageIndex = 0,
  isLandscape = false,
  containerWidth = 595,
  containerHeight = 842,
  showFilled = false,
}) => {
  const containerRef = useRef(null);
  const boxRefs = useRef({});
  const dispatch = useDispatch();
  const selectedForm = useSelector(getSelectedFormEditorForm);
  const formEditorControlData = useSelector(getFormEditorControlData);

  const handleClick = (e, id) => {
    e.stopPropagation();
    if (e.shiftKey) {
      dispatch(shiftSelectInputFields({ id, pageIndex: selectedPageIndex }));
    } else if (e.ctrlKey || e.metaKey) {
      dispatch(toggleSelectInputField({ id }));
    } else {
      dispatch(selectInputField({ id }));
    }
  };

  const normalizeIndexes = (pageInputFields) => {
    const sortedFields = sortBy(pageInputFields, 'originalIndex');
    return sortedFields.map((field, index) => ({
      ...field,
      originalIndex: index + 1,
    }));
  };

  useEffect(() => {
    if (!selectedForm?.inputFields?.[selectedPageIndex]) return;

    const currentFields = selectedForm.inputFields[selectedPageIndex];
    const normalizedFields = normalizeIndexes(currentFields);

    const needsUpdate = !isEqual(
      currentFields.map((f) => f.originalIndex),
      normalizedFields.map((f) => f.originalIndex)
    );

    if (needsUpdate) {
      normalizedFields.forEach((field) => {
        dispatch(
          onInputFieldChange({
            updatedInputField: field,
            pageIndex: selectedPageIndex,
          })
        );
      });
    }
  }, [
    selectedForm?.inputFields?.[selectedPageIndex]?.length,
    selectedPageIndex,
  ]);

  const capturePosition = () => {
    if (!formEditorControlData?.selectedInputFieldId) return;

    const el = boxRefs.current[formEditorControlData?.selectedInputFieldId];
    if (!el) return;

    const x = parseFloat(el.style.left);
    const y = parseFloat(el.style.top);
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    const original = selectedForm?.inputFields?.[selectedPageIndex]?.find(
      (item) => item.id === formEditorControlData?.selectedInputFieldId
    );
    if (!original) return;

    const updated = {
      ...original,
      x,
      y,
      width,
      height,
    };

    dispatch(
      onInputFieldChange({
        updatedInputField: updated,
        pageIndex: selectedPageIndex,
      })
    );
  };

  const radioGroups = React.useMemo(() => {
    return (
      selectedForm?.inputFields?.[selectedPageIndex]?.reduce((groups, item) => {
        if (item.type?.name === 'radioButton' && item.parentTitle) {
          const groupKey =
            typeof item.parentTitle === 'object'
              ? item.parentTitle.title
              : item.parentTitle;
          if (!groups[groupKey]) {
            groups[groupKey] = [];
          }
          groups[groupKey].push(item);
        }
        return groups;
      }, {}) || {}
    );
  }, [selectedForm, selectedPageIndex]);

  if (showFilled) {
    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {selectedForm?.inputFields?.[selectedPageIndex]?.map((item, index) => {
          const typeName = item.type?.name;
          const isCheckOrRadio =
            typeName === 'checkBox' || typeName === 'radioButton';
          const isImageType =
            typeName === 'signature' ||
            typeName === 'image' ||
            typeName === 'imageWithCrop' ||
            typeName === 'imageAsAttachment';
          const displayText = getFilledPreviewText(item);

          if (isImageType && !displayText) {
            return (
              <div
                key={item.id}
                style={{
                  position: 'absolute',
                  top: `${item.y}px`,
                  left: `${item.x}px`,
                  width: `${item.width}px`,
                  height: `${item.height}px`,
                  border: '1px dashed rgba(0,0,0,0.2)',
                  boxSizing: 'border-box',
                }}
              />
            );
          }

          return (
            <div
              key={item.id}
              style={{
                position: 'absolute',
                top: `${item.y}px`,
                left: `${item.x}px`,
                width: `${item.width}px`,
                height: `${item.height}px`,
                color: 'black',
                fontSize: `${item.textSize ?? 14}px`,
                display: isCheckOrRadio ? 'flex' : 'block',
                alignItems: isCheckOrRadio ? 'center' : 'flex-start',
                justifyContent: isCheckOrRadio ? 'center' : 'flex-start',
                fontWeight: isCheckOrRadio ? 'bold' : 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {displayText}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
    >
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {Object.values(radioGroups).flatMap((group, groupIndex) => {
          if (group.length < 2) return [];
          return group.slice(1).map((item, index) => {
            const prevItem = group[index];
            return (
              <line
                key={`${groupIndex}-${index}`}
                x1={prevItem.x + prevItem.width / 2}
                y1={prevItem.y + prevItem.height / 2}
                x2={item.x + item.width / 2}
                y2={item.y + item.height / 2}
                stroke="#4ad3ff"
                strokeWidth="2"
                strokeDasharray="5,5"
                opacity="0.7"
              />
            );
          });
        })}
      </svg>
      {selectedForm?.inputFields?.[selectedPageIndex]?.map((item, index) => (
        <div
          key={item.id}
          ref={(el) => (boxRefs.current[item.id] = el)}
          onClick={(e) => handleClick(e, item.id)}
          style={{
            position: 'absolute',
            top: `${item.y}px`,
            left: `${item.x}px`,
            width: `${item.width}px`,
            height: `${item.height}px`,
            backgroundColor: `rgba(20, 123, 164, ${formEditorControlData.inputFieldOpacity})`,
            color: 'black',
            // No padding or border: the text has to sit exactly where the
            // filled form and the PDF draw it, at the field's top left corner.
            boxShadow: formEditorControlData.selectedInputFieldIds?.includes(
              item.id
            )
              ? 'inset 0 0 0 2px #79ffe1'
              : 'inset 0 0 0 1px #004d6b',
            boxSizing: 'border-box',
            cursor: 'move',
            userSelect: 'none',
            zIndex:
              formEditorControlData?.selectedInputFieldId === item.id ? 10 : 1,
            fontSize: `${item.textSize ?? 14}px`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              left: '-8px',
              width: '20px',
              height: '20px',
              backgroundColor: '#4ad3ff',
              color: '#0f1220',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              border: '2px solid #fff',
              zIndex: 1,
            }}
          >
            {(() => {
              if (item.parentTitle && typeof item.parentTitle === 'object') {
                return `${item.originalIndex || index + 1}.${item.parentTitle.groupNumber
                  }`;
              }
              return item.originalIndex || index + 1;
            })()}
          </div>
          {item.type?.name === 'runningNumber'
            ? getFilledPreviewText(item)
            : item.text}
          {(item.type?.name === 'radioButton' ||
            item.type?.name === 'checkBox') && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#004d6b',
                  pointerEvents: 'none',
                }}
              >
                ✕
              </div>
            )}
        </div>
      ))}

      {formEditorControlData?.selectedInputFieldId && (
        <Moveable
          target={boxRefs.current[formEditorControlData?.selectedInputFieldId]}
          container={null}
          draggable
          resizable
          bounds={{
            left: 0,
            top: 0,
            right: containerWidth,
            bottom: containerHeight,
          }}
          onDrag={({ target, left, top }) => {
            target.style.left = `${left}px`;
            target.style.top = `${top}px`;
          }}
          onDragEnd={capturePosition}
          onResize={({ target, width, height, drag }) => {
            target.style.width = `${width}px`;
            target.style.height = `${height}px`;
            target.style.transform = drag.transform;
          }}
          onResizeEnd={() => {
            capturePosition();
          }}
          renderDirections={['e', 's', 'se']}
          keepRatio={false}
          snappable
          originDraggable={true}
          originRelative={false}
        />
      )}
      <style>
        {`
          ${(() => {
            const selectedItem = selectedForm?.inputFields?.[
              selectedPageIndex
            ]?.find(
              (item) => item.id === formEditorControlData?.selectedInputFieldId
            );
            if (
              selectedItem?.type?.name === 'radioButton' ||
              selectedItem?.type?.name === 'checkBox'
            ) {
              return '.moveable-origin { display: none !important; }';
            }
            return '';
          })()}
        `}
      </style>
    </div>
  );
};
